import { Options, Vue } from "vue-class-component"
import { Prop } from "vue-property-decorator"
import { Editor } from "@tiptap/core"
import { Node as ProseMirrorNode } from "@tiptap/pm/model"
import { NodeViewWrapper } from "@tiptap/vue-3"
import { Action, Getter } from "s-vuex-class"

import { getHrefByType } from "@/components/Reference/helpers/getHrefByType"

import { Account, Item, Project, Task, Team } from "@/models"
import { ReferenceType } from "@/plugins/extensions/ReferenceType"
import { router } from "@/router"
import { Actions } from "@/store"
import { Getters } from "@/store/getters"

const components = {
  NodeViewWrapper
}

@Options({
  name: "ReferenceComponentCommon",
  components
})
export default class extends Vue {
  @Getter(Getters.GET_SESSION_ACCOUNT) account: Account

  @Getter(Getters.GET_SESSION_PROJECT_ID) readonly projectId: string
  @Getter(Getters.GET_SESSION_TEAM) team: Team
  @Getter(Getters.GET_ITEM) currentReferenceItem: Item

  @Action(Actions.GET_PROJECT) getProject: ({
    projectId,
    onlyGet
  }: {
    projectId: string
    onlyGet?: boolean
  }) => Promise<Project>

  @Prop() node: ProseMirrorNode
  @Prop() editor: Editor
  @Prop() updateAttributes: (attributes: Record<string, any>) => void
  @Prop() getPos: () => number

  nodeViewWrapper: any = "span"

  safeUpdateAttributes(attributes: Record<string, any>) {
    try {
      this.updateAttributes(attributes)
    } catch (error) {
      // Node was deleted before async operation completed (RangeError)
      // Log in development to help diagnose real bugs
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn("[safeUpdateAttributes] Failed to update node attributes:", error)
      }
    }
  }

  get text() {
    return this.node.attrs.linkText
  }

  get originUrl() {
    return this.node.attrs.originUrl
  }

  get href() {
    return this.node.attrs.linkHref
  }

  get isShowLoading() {
    return this.node.attrs.loading
  }

  get isShowDetails() {
    return this.node.attrs.showDetails
  }

  get info() {
    try {
      return JSON.parse(this.node.attrs.info)
    } catch {
      return false
    }
  }

  get type() {
    return this.node.attrs.configType
  }

  get referenceTypes() {
    return ReferenceType
  }

  get taskEstimatedHours() {
    const taskId = this.info?.id
    if (taskId) {
      const currentItemTask = this.currentReferenceItem?.tasks?.find(
        (task: Task) => task.id === taskId
      )
      if (
        currentItemTask?.estimatedHours !== null &&
        currentItemTask?.estimatedHours !== undefined
      ) {
        return currentItemTask.estimatedHours
      }
    }

    if (this.info?.estimatedHours !== null && this.info?.estimatedHours !== undefined) {
      return this.info.estimatedHours
    }

    return undefined
  }

  get taskReferenceDescription() {
    if (!this.info?.typeParsed?.label) return ""

    const taskEstimatedHours = this.taskEstimatedHours

    if (taskEstimatedHours !== undefined && taskEstimatedHours !== null) {
      return `${this.info.typeParsed.label}, ${taskEstimatedHours} hr`
    }

    return this.info.typeParsed.label
  }

  get route() {
    return router.resolve(this.originUrl.replace(location.origin.toString(), ""))
  }

  replaceWithPlainLink(url: string) {
    if (!this.editor || !this.getPos) return false
    if (!url) return false
    const { state, view } = this.editor
    const { schema } = state
    const linkMark = schema.marks.link
    const textNode = schema.text(
      url,
      linkMark ? [linkMark.create({ href: url, target: "_blank" })] : undefined
    )
    const pos = this.getPos()
    const tr = state.tr.replaceWith(pos, pos + this.node.nodeSize, textNode)
    view.dispatch(tr)
    return true
  }

  created() {
    if (this.editor) {
      this.nodeViewWrapper = NodeViewWrapper
    }
  }

  async getHrefByType(type, info?) {
    const accountName = this.account.urlName
    const referenceInfo = info || this.info
    if (type === ReferenceType.USER || type === ReferenceType.PREVIOUS_TASK_OWNER) {
      return getHrefByType(type, referenceInfo, accountName, "")
    }
    let projectId = referenceInfo?.projectId || this.projectId
    if (type === ReferenceType.PROJECT) {
      projectId = referenceInfo.id || projectId
    }
    if (!projectId) {
      return ""
    }
    const projectName = (
      await this.getProject({
        projectId,
        onlyGet: true
      })
    ).urlName

    if (projectName) {
      return getHrefByType(type, info || this.info, accountName, projectName)
    }
    return ""
  }
}
